/**
 * Centralized Environment Configuration
 * 
 * This module handles runtime environment variables for both:
 * - Local development (via Vite's import.meta.env from .env files)
 * - Production deployments (via window.ENV injected at container startup)
 * 
 * Usage:
 *   import { env } from '@/config/env';
 *   console.log(env.API_URL);
 */

// Type for runtime environment configuration (injected at container startup)
declare global {
  interface Window {
    ENV?: Record<string, string>;
  }
}

type ImportMetaEnvRecord = {
  env?: Record<string, string>;
};

/**
 * Resolves an environment variable from multiple sources.
 * Priority: window.ENV (runtime) > import.meta.env (build-time) > process.env (Node.js)
 */
const resolveEnvVar = (key: string): string | undefined => {
  // Priority 1: Check runtime window.ENV (injected at container startup)
  try {
    if (typeof window !== 'undefined' && window.ENV) {
      if (window.ENV[key]) return window.ENV[key];
    }
  } catch {
    // ignore - window may not be defined in SSR contexts
  }

  // Priority 2: Check import.meta.env (Vite build-time variables)
  try {
    const metaEnv =
      typeof import.meta !== 'undefined'
        ? (import.meta as ImportMetaEnvRecord).env
        : undefined;
    if (metaEnv && metaEnv[key]) {
      return metaEnv[key];
    }
  } catch {
    // ignore
  }

  // Priority 3: Check process.env (Node.js environment)
  try {
    if (typeof process !== 'undefined' && process.env && process.env[key]) {
      return process.env[key] as string;
    }
  } catch {
    // ignore
  }

  return undefined;
};

/**
 * Environment configuration object with all application environment variables.
 * Values are resolved once at module load time.
 */
/**
 * NOTE: Sensitive upstream identifiers (project id, collection keys, service
 * ids, component id) are intentionally NOT part of the frontend env anymore.
 * They live only in the landing backend, which injects them when proxying
 * requests to the upstream `/entities/*` API. The frontend only needs to know
 * how to reach the APIs.
 */
export const env = {
  // API Configuration
  API_URL: resolveEnvVar('VITE_API_URL') || 'http://localhost:8000',
  LANDING_API_URL:
    resolveEnvVar('VITE_LANDING_API_URL') ||
    resolveEnvVar('VITE_API_URL') ||
    'http://localhost:8000',
} as const;

/**
 * Type for the environment configuration object
 */
export type EnvConfig = typeof env;
